import { useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBox, MiChip } from "@miplatform/components";
import { RequirementType } from "@miplatform/types";
import { MiPrintFooter } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { BidPrintHeader } from "./BidPrintHeader";
import { useReviewViewModel } from "./useReviewViewModel";

const getRequirementChipLabel = (type?: RequirementType | string) => {
  if (type === "Inspection") {
    return "City Requirement";
  }
  return `${type} Requirement`;
};

const getRequirementGroupTitle = (
  type?: RequirementType | string,
  cityName?: string,
  communityName?: string
) => {
  if (type === "Inspection") {
    return `${cityName} City Requirements`;
  }
  if (type === "Community") {
    return `${communityName} Community Requirements`;
  }
  if (type === "Builder") {
    return "Builder Requirement";
  }
  return "";
};

export function Review() {
  const {
    planAreas,
    requirements,
    tenant,
    materials,
    communityName,
    cityName,
    getTenant,
    fetchMaterials,
  } = useReviewViewModel();
  const { classes, cx } = useStyles();

  useEffect(() => {
    getTenant();
    fetchMaterials();
  }, []);

  const groupedRequirements = useMemo(() => {
    const order: RequirementType[] = ["Inspection", "Community", "Builder"];
    return order
      .map((type) => {
        const typeRequirements = requirements.filter((r) => r.type === type);
        return {
          type,
          requirements: typeRequirements,
        };
      })
      .filter((group) => group.requirements.length > 0);
  }, [requirements]);

  return (
    <MiBox headerTitle="Review Bid">
      <BidPrintHeader tenant={tenant} />
      <Box className={classes.root}>
        {/* left/item section */}
        <Box className={classes.itemsSection}>
          <Typography className={classes.sectionTitle}>Items</Typography>
          {planAreas.map((area) => (
            <Box className={classes.area} key={area.id}>
              <Typography className={classes.areaTitle}>
                {area.label}
              </Typography>
              {area.materials?.map((am) => {
                const materialDetails = materials.find(
                  (item) => item.id === am.materialId
                );

                return (
                  <Box className={classes.materialRow} key={am.id}>
                    <Typography className={classes.category}>
                      {am.quantity} {am.categoryName}
                    </Typography>
                    <Typography
                      className={cx(
                        classes.description,
                        am.suppliedByBuilder && classes.suppliedBuilderText
                      )}
                    >
                      {am.suppliedByBuilder
                        ? "SUPPLIED BY BUILDER"
                        : am.itemDescription}
                      {materialDetails?.fieldNote && (
                        <span className={classes.fieldNote}>
                          {` - ${materialDetails?.fieldNote}`}
                        </span>
                      )}
                    </Typography>
                  </Box>
                );
              })}
              {requirements
                .filter((requirement) =>
                  requirement.materials?.some((am) =>
                    am.areaTypeIds?.includes(area.areaTypeId ?? "")
                  )
                )
                .map((requirement) => {
                  return (
                    <Box
                      className={classes.materialRow}
                      key={`${requirement.id}-${area.id}`}
                    >
                      <Typography className={classes.category}>
                        1 {requirement.name}
                      </Typography>
                      <Box className={classes.requirementDescription}>
                        <Typography
                          className={classes.requirementItemDescription}
                        >
                          {requirement.name}
                        </Typography>
                        <MiChip
                          className={classes.requirementChip}
                          label={getRequirementChipLabel(requirement.type)}
                          size="small"
                        />
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          ))}
        </Box>
        {/* right/pricing breakdown Section */}
        <Box className={classes.pricingSection}>
          <Typography className={classes.sectionTitle}>
            Bid Pricing Breakdown
          </Typography>
          {groupedRequirements.map((group) => (
            <Box className={classes.requirementGroup} key={group.type}>
              <Typography className={classes.requirementGroupTitle}>
                {getRequirementGroupTitle(group.type, cityName, communityName)}
              </Typography>
              {group.requirements.map((requirement) => (
                <Box
                  className={classes.requirementPricingRow}
                  key={requirement.id}
                >
                  <Typography
                    className={classes.requirementPricingItemDescription}
                  >
                    {requirement.name}
                  </Typography>
                  <MiChip
                    className={classes.requirementChip}
                    label={getRequirementChipLabel(group.type)}
                    size="small"
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
      <MiPrintFooter
        invoiceTermsAndConditionsHtml={tenant?.invoiceTermsAndConditionsHtml}
        licenses={tenant?.licenses}
      />
    </MiBox>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: 4,
    flex: 1,
  },
  itemsSection: {
    flex: 1,
    minWidth: 0,
    marginTop: theme.spacing(2),
  },
  pricingSection: {
    flex: 0.8,
    minWidth: 0,
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.small,
  },
  area: {
    marginTop: theme.spacing(0.75),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  areaTitle: {
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.grey.accent,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.small,
  },
  materialRow: {
    display: "grid",
    gridTemplateColumns: "minmax(150px, 30%) minmax(0, 1fr)",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  category: {
    padding: theme.spacing(0.5, 1),
    borderRight: `1px solid ${theme.palette.divider}`,
    textTransform: "uppercase",
    fontSize: theme.fontSize.small,
  },
  description: {
    padding: theme.spacing(0.5, 1),
    textTransform: "uppercase",
    fontSize: theme.fontSize.small,
  },
  requirementChip: {
    height: 16,
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.main,
    textTransform: "none",
    borderRadius: 4,
  },
  fieldNote: {
    color: theme.palette.red.main,
    fontSize: theme.fontSize.small,
  },
  requirementDescription: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    minWidth: 0,
  },
  requirementItemDescription: {
    textTransform: "uppercase",
    fontSize: theme.fontSize.small,
  },
  suppliedBuilderText: {
    color: theme.palette.red.main,
  },
  requirementGroup: {
    marginTop: theme.spacing(0.75),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  requirementGroupTitle: {
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.secondary.light,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.small,
  },
  requirementPricingRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  requirementPricingItemDescription: {
    fontSize: theme.fontSize.small,
  },
}));
